<template>
    <PageWrapper title="Users & Roles" :description="description">
        <template #headerActions>
            <NewButton
                class="UsersDashboard__inviteButton"
                variant="fill"
                color="primary"
                @click="startInvite"
            >
                <EidIcon :icon-src="faPlus" />Add new user
            </NewButton>
        </template>

        <InfiniteScroll @scroll="loadMore">
            <UsersTable
                :users="users"
                :can-load-more="canLoadMore"
                :loading-more="loadingMore"
                :current-user-email="currentUserEmail"
                :dynamic-creation-enabled="dynamicCreationEnabled"
                :sso-connection-name="ssoConnectionName"
                @edit="startEdit"
                @delete="startDelete"
                @resend-invite="startResendInvite"
                @change-role="startRoleChange"
            />
        </InfiniteScroll>
    </PageWrapper>
</template>

<style lang="scss">
.UsersDashboard {
    &__inviteButton.Button {
        padding: 9px 28px;
        min-width: 165px;
        margin-left: 15px;
        white-space: nowrap;
    }

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: nowrap;
    }
}
</style>

<script lang="ts">
    import { Component, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { faPlus } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { InfiniteScroll } from '@evidentid/dashboard-commons/components/InfiniteScroll';
    import { RbacFeature, RbacRole } from '@evidentid/tprm-portal-lib/models/rbac';
    import { User } from '@evidentid/tprm-portal-lib/models/user-management/User.model';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import NewButton from '@/components/new-button/NewButton.vue';
    import PageWrapper from '@/components/page-wrapper/PageWrapper.vue';
    import { usePageLayoutStore } from '@/modules/page-layout/store';

    import UsersTable from '../components/UsersTable.vue';
    import { UserManagementProcedure } from '../types';
    import { emptyUsersListStatus, UserManagementUsersList } from '../store';
    import { useUserStore } from '@evidentid/dashboard-commons/modules/auth/store/user';
    import { useUserManagementStore } from '@/modules/user-management/store';
    import { useSsoProvisioningStore } from '@/modules/sso-provisioning/store';
    import { useRbacStore } from '@evidentid/dashboard-commons/modules/role-base-access-control/store';

    @Component({
        components: { UsersTable, InfiniteScroll, NewButton, PageWrapper, EidIcon },
    })
    export default class UsersDashboard extends Vue {
        private get pageLayoutStore() {
            return usePageLayoutStore();
        }

        private faPlus = faPlus;
        private description = 'Add and manage users, assign roles, and control access so each user sees only what they need.';

        private created(): void {
            this.pageLayoutStore.resetPageLayout();
        }

        private get state(): UserManagementUsersList {
            return useUserManagementStore().usersList[this.rpName] || emptyUsersListStatus;
        }

        private get rpName(): string {
            return this.$route.params.rpId as string;
        }

        private get rpDisplayName(): string | null {
            const relyingParties = useUserStore().relyingParties || [];
            const relyingParty = relyingParties.find((x: any) => x.name === this.rpName);
            return relyingParty.displayName || null;
        }

        private get currentUserEmail(): string | null {
            return useUserStore().data?.email || null;
        }

        // Absent (still loading) or null (load failed) both read as off, which keeps every role editable.
        private get dynamicCreationEnabled(): boolean {
            return useSsoProvisioningStore().view[this.rpName]?.dynamicCreationEnabled === true;
        }

        // The Auth0 connection that federates THIS relying party's logins, which is the only one
        // the Action governs roles on. Null while the SSO view is loading or after it failed, and
        // the row treats null as "governed by nothing" -- the direction that keeps roles editable.
        private get ssoConnectionName(): string | null {
            return useSsoProvisioningStore().view[this.rpName]?.connectionName || null;
        }

        private get users(): User[] {
            return this.state.users;
        }

        private get loadingMore(): boolean {
            return this.state.status === OperationStatus.loading;
        }

        private get canLoadMore(): boolean {
            return this.state.canLoadMore;
        }

        @Watch('rpName', { immediate: true })
        private async onRpChange(newVal: string, oldVal: string): Promise<void> {
            if (newVal && newVal !== oldVal) {
                await useRbacStore().validateRbac([ RbacFeature.configurationUsersAndRoles ]);
                // Reuses the SSO store's loader; a view already loaded (or failed) is not refetched.
                if (useSsoProvisioningStore().view[newVal] === undefined) {
                    await useSsoProvisioningStore().load(newVal, { quiet: true });
                }
            }
        }

        private mounted(): void {
            this.loadMore();
        }

        private async loadMore(): Promise<void> {
            if (!this.state.canLoadMore || this.state.status === OperationStatus.loading) {
                return;
            }

            await useUserManagementStore().loadMoreUsers({ rpName: this.rpName });
        }

        private startInvite(): void {
            const options = { rpName: this.rpName, rpDisplayName: this.rpDisplayName };
            (this as any).$procedures.execute(UserManagementProcedure.invite, options, this.finish.bind(this));
        }

        private startDelete(user: User): void {
            const options = { rpName: this.rpName, user };
            (this as any).$procedures.execute(UserManagementProcedure.delete, options, this.finish.bind(this));
        }

        private startEdit(user: User): void {
            const options = { rpName: this.rpName, user };
            (this as any).$procedures.execute(UserManagementProcedure.edit, options, this.finish.bind(this));
        }

        private startResendInvite(user: User): void {
            const options = { rpName: this.rpName, user };
            (this as any).$procedures.execute(UserManagementProcedure.resendInvite, options, this.finish.bind(this));
        }

        private startRoleChange(user: User, newRole: RbacRole): void {
            const options = { rpName: this.rpName, user, newRole };
            (this as any).$procedures.execute(
                UserManagementProcedure.roleChangeConfirmation, options, this.finish.bind(this),
            );
        }

        private async finish(modified: boolean): Promise<void> {
            if (modified) {
                const rpName = this.rpName;
                await useUserManagementStore().resetUsersList({ rpName });
                await useUserManagementStore().loadMoreUsers({ rpName });
            }
        }
    }
</script>
